import { useCallback, useEffect, useMemo, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DebouncedCallback<T extends (...args: any[]) => any> = ((
  ...args: Parameters<T>
) => void) & {
  cancel: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): DebouncedCallback<T> {
  const timeoutRef = useRef<number | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debouncedCallback = useMemo<DebouncedCallback<T>>(() => {
    const fn = (...args: Parameters<T>) => {
      cancel();
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        callbackRef.current(...args);
      }, delay);
    };
    fn.cancel = cancel;
    return fn;
  }, [callback, cancel, delay]);

  useEffect(() => cancel, [cancel]);

  return debouncedCallback;
}
